import { ResponseError } from '@/data';
import axios from 'axios';

const ACCESS_TOKEN_KEY = "access_token";
const REFRESH_TOKEN_KEY = "refresh_token";
const KEEP_USER_LOGGED_IN_KEY = "keepUserLoggedIn";

const getAccessToken = () => {
    return (
        sessionStorage.getItem(ACCESS_TOKEN_KEY) ||
        localStorage.getItem(ACCESS_TOKEN_KEY) ||
        undefined
    );
}

const getKeepUserLoggedIn = () => {
    return localStorage.getItem(KEEP_USER_LOGGED_IN_KEY) === "true";
}

const setKeepUserLoggedIn = (keepUserLoggedIn: boolean) => {
    localStorage.setItem(KEEP_USER_LOGGED_IN_KEY, keepUserLoggedIn.toString());
}

const setAccessToken = (token: string, keepUserLoggedIn = true) => {
    setKeepUserLoggedIn(keepUserLoggedIn);

    if (keepUserLoggedIn) {
        localStorage.setItem(ACCESS_TOKEN_KEY, token);
        sessionStorage.removeItem(ACCESS_TOKEN_KEY);
        return;
    }

    sessionStorage.setItem(ACCESS_TOKEN_KEY, token);
    localStorage.removeItem(ACCESS_TOKEN_KEY);
}

/** Store access + optional refresh (e.g. reset-password / login-style payloads). */
const applyAuthTokens = (
    accessToken: string,
    refreshToken: string | undefined,
    keepUserLoggedIn = true,
) => {
    setAccessToken(accessToken, keepUserLoggedIn);
    if (!refreshToken) return;
    if (keepUserLoggedIn) {
        localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
        sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    } else {
        sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
    }
};

const clearAccessToken = () => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(KEEP_USER_LOGGED_IN_KEY);
    sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
}

const api = axios.create({
    baseURL: import.meta.env.VITE_BASE_API_URL,
    headers: {
        "Content-Type": "application/json",
    },
    withCredentials: true
});

//Set access token for requests
api.interceptors.request.use(
    (config) => {
        const accessToken = getAccessToken();
        if (accessToken){
            config.headers["Authorization"] = `Bearer ${accessToken}`;
        }
        
        return config;
    },
    (error) => Promise.reject(error)
);

let refreshingPromise: any = null;
api.interceptors.response.use(
    async (response) => {
        return Promise.resolve(response);
    },
    async (error) => {
        if (error.status === 422)
            throw new ResponseError(error.response.data.errors, error.response.data.message);
        
        if(error.status === 401 && !refreshingPromise){
            const accessToken = getAccessToken();
            if (accessToken) {
                try{
                    refreshingPromise = await api.get("/refresh");
                    setAccessToken(refreshingPromise.data.access_token);
                    error.response.config.headers["Authorization"] = `Bearer ${refreshingPromise.data.access_token}`;
                    return axios(error.response.config);
                }catch(refreshError){
                    clearAccessToken();
                    return Promise.reject(refreshError)
                } finally {
                    refreshingPromise = null;
                }
            }
        }
        return Promise.reject(error)
    }
);


export default {
    get: api.get,
    post: api.post,
    put: api.put,
    delete: api.delete,
    getAccessToken,
    getKeepUserLoggedIn,
    setKeepUserLoggedIn,
    setAccessToken,
    applyAuthTokens,
    clearAccessToken,
};
